import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Target, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { usePanel } from "@/context/PanelContext";

// Time-horizon buckets keyed by sprint length in days.
const HORIZONS = [
  { id: "week",   label: "1-week sprints",   short: "1w",  max: 7   },
  { id: "twoweek", label: "2-week sprints",  short: "2w", min: 8,  max: 21  },
  { id: "month",  label: "30-day goals",     short: "30d", min: 22, max: 45  },
  { id: "quarter", label: "90-day goals",    short: "90d", min: 46, max: 135 },
  { id: "year",   label: "1-year goals",     short: "1y",  min: 136             },
];

function sprintLengthDays(g) {
  if (!g.sprint_start || !g.sprint_end) return null;
  const start = new Date(`${g.sprint_start}T00:00:00`);
  const end = new Date(`${g.sprint_end}T00:00:00`);
  return Math.max(1, Math.round((end - start) / 86400000));
}

function bucketFor(g) {
  const days = sprintLengthDays(g);
  if (days == null) return "twoweek";
  for (const h of HORIZONS) {
    if (h.max == null && days >= (h.min || 0)) return h.id;
    if (days <= h.max && days >= (h.min || 0)) return h.id;
  }
  return "twoweek";
}

export default function GoalsPage() {
  const { openNew } = useOutletContext();
  const { open: openPanel, refreshKey } = usePanel();
  const [goals, setGoals] = useState([]);

  useEffect(() => {
    api.get("/goals").then((r) => setGoals(r.data));
  }, [refreshKey]);

  const grouped = useMemo(() => {
    const map = Object.fromEntries(HORIZONS.map((h) => [h.id, []]));
    goals.forEach((g) => {
      const b = bucketFor(g);
      (map[b] || map.twoweek).push(g);
    });
    return map;
  }, [goals]);

  return (
    <div className="px-6 md:px-10 pt-6 max-w-5xl mx-auto" data-testid="goals-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Goals</h1>
          <p className="text-slate-500 mt-1">Organized by horizon — pick a sprint length when creating each goal.</p>
        </div>
        <button onClick={openNew} className="tg-btn-primary" data-testid="goals-new">
          <Plus className="h-4 w-4" /> New goal
        </button>
      </div>

      {goals.length === 0 ? (
        <div className="tg-card p-8 text-center text-slate-400 text-sm">
          No goals yet. Click <span className="font-semibold">New goal</span> to set your first one.
        </div>
      ) : (
        HORIZONS.map((h) => {
          const list = grouped[h.id] || [];
          if (list.length === 0) return null;
          return (
            <section key={h.id} className="mb-8" data-testid={`goal-section-${h.id}`}>
              <div className="flex items-baseline gap-2 mb-3">
                <h2 className="font-display text-lg font-bold text-slate-800">{h.label}</h2>
                <span className="text-xs text-slate-400">{list.length} goal{list.length === 1 ? "" : "s"}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {list.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => openPanel("goal", g)}
                    className="tg-card p-5 text-left hover:shadow-md hover:border-slate-300 transition-all"
                    data-testid={`goal-card-${g.id}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="tg-stat-icon bg-emerald-50">
                        <Target className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-slate-900 truncate">{g.title}</div>
                        {g.description && <div className="text-sm text-slate-500 mt-0.5 line-clamp-2">{g.description}</div>}
                      </div>
                      <span className="tg-pill text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0">
                        {h.short}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                      <span>{g.sprint_start} → {g.sprint_end}</span>
                      <span className="font-semibold text-slate-700">{g.progress || 0}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${g.progress || 0}%` }}
                      />
                    </div>
                  </button>
                ))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
